import {
    ReadonlyVec2,
    ReadonlyVec3,
    ReadonlyVec4,
    mat4,
    quat,
    vec2,
    vec3,
    vec4,
} from 'gl-matrix';
import { gl } from '../../../Engine';
import { SignedDistanceField } from '../../../core/models/SignedDistanceField';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';
import { UniformMatrix } from '../../../core/utils/uniforms/UniformMatrix';
import { UniformSampler } from '../../../core/utils/uniforms/UniformSampler';
import { UniformVec2 } from '../../../core/utils/uniforms/UniformVec2';
import { UniformVec3 } from '../../../core/utils/uniforms/UniformVec3';
import { UniformVec4 } from '../../../core/utils/uniforms/UniformVec4';
import { TextureView } from '../../../view/TextureView';
import { PlaneView } from '../../../view/views/PlaneView';
import { ImageObject } from '../../image/ImageObject';
import { ContourTexture } from '../../structure/ContourTexture';
import { RotatedBoundingBox } from '../RotatedBoundingBox';
import { CustomTargetObject } from './CustomTargetObject';

/**
 * {@link CustomTargetObject} that adapts to the data of a {@link ImageObject} by generating an adapted sphere around a given center point.\
 * The sphere is generated using a flood fill algorithm, which is implemented in the {@link FloodFillShader}.\
 * The {@link FloodFillShader} is used to generate a {@link SignedDistanceField} that represents the sphere adapted to target {@link ImageObject}'s contents.
 */
export class AdaptiveSphere extends CustomTargetObject {
    /**
     * The outline width in pixels.
     */
    private static readonly OUTLINE_WIDTH = 1;

    /**
     * The amount of padding added to each side of the contour data in millimeters.\
     * This is useful to support better visualization as it provides smoother contours.
     */
    protected margin: number;

    /**
     * Value that determines the planar distance between neighboring pixels along x and y -axes in millimeters.\
     * The unit is mm/px.
     */
    protected resolution: number;

    /**
     * Diameter of the sphere in millimeters.
     */
    protected diameter: number;

    /**
     * {@link RotatedBoundingBox} that defines the dimensions of the sphere.\
     * The scale is 0.5 * diameter + margin.
     */
    protected readonly _boundingBox: RotatedBoundingBox;

    /**
     * {@link FrameBufferObject} used to {@link updateTexture update the texture} of this {@link AdaptiveSphere}.
     */
    protected fbo?: FrameBufferObject;

    /**
     * {@link Array} containing 2 {@link SignedDistanceField} objects.\
     * These are used when {@link updateTexture updating the texture}. And one of them represents the texture content of this {@link AdaptiveSphere}.\
     * Adaptive sphere is generated using a flood fill algorithm,\
     * which performs multiple render cycles, during which it reads data from one {@link SignedDistanceField} and writes to the other.
     */
    protected buffers: SignedDistanceField[] = [];

    /**
     * Index of the {@link SignedDistanceField} in the {@link buffers} {@link Array} that is currently being read from.\
     * This is used when updating the texture to ping-pong between the {@link SignedDistanceField} objects.
     */
    protected readIndex = 0;

    /**
     * {@link SphereShader} used to render the adaptive sphere sdf data on the {@link sdf} {@link WebGLTexture}.
     */
    protected readonly shader: FloodFillShader;

    /**
     * {@link OutlineShader} used to render the sphere outline.\
     * This works similarly to {@link ContourRenderer} in that it reads the {@link sdf} {@link WebGLTexture} within the fragment shader.\
     * It then checks if the current pixel is within the sphere and if it is, it checks if any of the neighboring pixels are outside the sphere.\
     * If any of the neighboring pixels are outside the sphere, the current pixel is colored .
     */
    private readonly outlineShader: OutlineShader;

    /**
     * {@link ImageObject} the data of which the {@link AdaptiveSphere} should adapt to.
     */
    protected readonly image: ImageObject;

    /**
     * {@link mat4} that converts from patient space to image space.\
     * This is used by the {@link outlineShader} to convert pixels from ViewSpace to TextureSpace.\
     * This conversion is performed to convert pixels from ViewSpace to {@link sdf} TextureSpace to see if the pixel is on the outline.
     */
    private readonly patientToImage: mat4;

    /**
     * {@link vec4 Color} of the sphere's outline.
     */
    private readonly color: vec4;

    /**
     * Value between 0 and 1 that determines how similar the neighboring pixels have to be to be considered part of the target volume.\
     * This is used by the {@link FloodFillShader} to determine if the neighboring pixels are similar enough to be considered part of the target volume.
     */
    private _similarityThreshold = 0.3;

    /**
     * {@link AdaptiveSphere} constructor.\
     * This constructor will create a {@link RotatedBoundingBox} around the given {@link center} with the given {@link diameter} and {@link margin}.\
     * It will then initialize the {@link ShaderProgram shaders} and {@link update} itself, which will generate the {@link mesh} and {@link sdf}.
     * @param target {@link ImageObject} that this {@link AdaptiveSphere} should adapt to.
     * @param center {@link ReadonlyVec3 PatientSpace coordinates} of the center of the {@link AdaptiveSphere}.
     * @param diameter Diameter of the {@link AdaptiveSphere} in millimeters.
     * @param resolution Resolution of the {@link AdaptiveSphere}'s {@link sdf} {@link WebGLTexture} in millimeters/pixel.
     * @param color {@link ReadonlyVec4 Color} of the {@link AdaptiveSphere}'s outline.
     * @param margin Amount of padding added to each side of the contour data in millimeters.\
     * This is required by other functions using the {@link sdf} data for consistency.
     */
    public constructor(
        target: ImageObject,
        center: ReadonlyVec3,
        diameter: number,
        resolution: number,
        color: ReadonlyVec4,
        margin: number = ContourTexture.DEFAULT_MARGIN
    ) {
        super(target);
        this.patientToImage = mat4.create();
        this.resolution = resolution;
        this.diameter = diameter;
        this.margin = margin;
        this.color = vec4.clone(color);
        this._boundingBox = new RotatedBoundingBox();
        this._boundingBox.expandWithPoint(center);
        this._boundingBox.addMargins(0.5 * diameter + margin);
        this.shader = new FloodFillShader();
        this.outlineShader = new OutlineShader();
        this.image = target;
        this.updateTexture();
    }

    /**
     * Method to render the outline of this {@link TargetSphere} on a {@link view}.
     * @throws Error if the {@link view} is not a {@link PlaneView}.
     * @param view {@link TextureView} on which the {@link TargetSphere} should be rendered.
     */
    public render(view: TextureView): void {
        if (view instanceof PlaneView) {
            if (this.buffers[this.readIndex]) {
                const vao = this._boundingBox.crossSection(
                    view.projectionViewMatrix
                );
                if (vao) {
                    this.outlineShader.start();
                    this.outlineShader.color.loadVec4(this.color);
                    this.outlineShader.relativeDist.loadVec2(
                        vec2.fromValues(
                            AdaptiveSphere.OUTLINE_WIDTH / 2 / view.size[0],
                            AdaptiveSphere.OUTLINE_WIDTH / 2 / view.size[1]
                        )
                    );

                    const viewToPatient = mat4.invert(
                        mat4.create(),
                        view.projectionViewMatrix
                    );
                    //remove translation
                    viewToPatient[12] =
                        viewToPatient[13] =
                        viewToPatient[14] =
                            0.0;

                    this.outlineShader.viewToTexture.loadMatrix(
                        mat4.multiply(
                            mat4.create(),
                            this.patientToImage,
                            viewToPatient
                        )
                    );
                    this.bindReadTexture();
                    vao.bind(0, 1);
                    gl.drawElements(
                        gl.TRIANGLES,
                        vao.indexCount,
                        gl.UNSIGNED_INT,
                        0
                    );
                    vao.unbind(0, 1);
                    vao.delete();
                }
            }
        } else {
            throw new Error(
                `Attempt to render ${this.constructor.name} on unsupported View of type ${view.constructor.name}`
            );
        }
    }

    /**
     * Method to {@link SignedDistanceField.bind bind} the {@link SignedDistanceField} stored in the {@link buffers} {@link Array} at index {@link readIndex}.
     * @returns True to indicate that the {@link SignedDistanceField} was successfully bound.
     */
    public bindReadTexture(): boolean {
        this.buffers[this.readIndex].bind();
        return true;
    }

    /**
     * Method to get the {@link _boundingBox RotatedBoundingBox} of this {@link TargetSphere}.
     */
    public get boundingBox(): RotatedBoundingBox {
        return this._boundingBox;
    }

    /**
     * Method to get the {@link planarPixelSize} of this {@link TargetSphere}.\
     * The {@link planarPixelSize} is the {@link RotatedBoundingBox.size size} of the {@link boundingBox} divided by the {@link resolution}.\
     * The {@link planarPixelSize} is rounded up to the nearest integer and then made odd.\
     * This is done to ensure that the flood fill algorithm used to {@link updateTexture} works correctly.
     * @returns The {@link planarPixelSize} of this {@link TargetSphere}.
     */
    public get planarPixelSize(): ReadonlyVec2 {
        const planarPixelSize = vec2.fromValues(
            this.boundingBox.size[0] / this.resolution,
            this.boundingBox.size[1] / this.resolution
        );
        vec2.ceil(planarPixelSize, planarPixelSize);
        planarPixelSize[0] =
            planarPixelSize[0] & 1
                ? planarPixelSize[0]
                : planarPixelSize[0] + 1;
        planarPixelSize[1] =
            planarPixelSize[1] & 1
                ? planarPixelSize[1]
                : planarPixelSize[1] + 1;
        return planarPixelSize;
    }

    public get pixelSize(): undefined | ReadonlyVec3 {
        return this.sdf?.size;
    }

    /**
     * Method to update the {@link diameter} of the {@link AdaptiveSphere}.\
     * This will also update the {@link _boundingBox} and trigger the {@link update} method,\
     * which will update the {@link mesh} and {@link sdf}.
     * @param size The new {@link diameter} in millimeters.
     */
    public resize(size: number): void {
        this.diameter = size;
        const bb = new RotatedBoundingBox();
        bb.expandWithPoint(this._boundingBox.center);
        bb.addMargins(0.5 * this.diameter + this.margin);
        this._boundingBox.set(bb);
        this.updateTexture();
    }

    public setPosition(position: ReadonlyVec3): void {
        this._boundingBox.translate(
            vec3.sub(vec3.create(), position, this._boundingBox.center)
        );
        this.updateTexture();
    }

    /**
     * Method to update the {@link sdf} of this {@link AdaptiveSphere}.\
     * This is automatically called from the {@link CustomTargetObject}'s {@link update}-method.\
     * This method will first update the size of the {@link buffers} and {@link fbo} if necessary.\
     */
    protected updateTexture(): void {
        if (!this._boundingBox.min || !this._boundingBox.max) {
            throw new Error(
                `Attempt to update the texture of AdaptiveSphere with an empty BoundingBox`
            );
        }
        const planarPixelSize = this.planarPixelSize;
        const boundingBoxSlices =
            Math.floor(
                this._boundingBox.size[2] / this.image.parent.grid.resZ
            ) + 2;
        const pixelSize = vec3.fromValues(
            planarPixelSize[0],
            planarPixelSize[1],
            boundingBoxSlices
        );
        if (this.fbo) {
            this.fbo.bind();
            if (!vec2.equals(this.fbo.size, planarPixelSize)) {
                this.fbo.resize(planarPixelSize);
                for (const buffer of this.buffers) {
                    buffer.resize(pixelSize);
                }
            } else if (this.buffers[0].size[2] !== boundingBoxSlices) {
                for (const buffer of this.buffers) {
                    buffer.resize(pixelSize);
                }
            } else {
                gl.viewport(0, 0, planarPixelSize[0], planarPixelSize[1]);
                for (let layer = 0; layer < pixelSize[2]; ++layer) {
                    this.fbo.createTextureLayer(this.buffers[1].texture, layer);
                    gl.clear(gl.COLOR_BUFFER_BIT);
                }
            }
        } else {
            this.fbo = new FrameBufferObject(planarPixelSize);
            this.buffers = [
                new SignedDistanceField(pixelSize),
                new SignedDistanceField(pixelSize),
            ];
            this.fbo.bind();
        }
        gl.viewport(0, 0, this.buffers[0].size[0], this.buffers[0].size[1]);
        //bind image on texture unit 1
        gl.activeTexture(gl.TEXTURE1);
        this.image.bindTexture();
        gl.activeTexture(gl.TEXTURE0);

        const vao = RenderStorage.getQuadVAO();
        vao.bind(0);

        this.shader.start();
        this.shader.brushResolution.loadVec3(
            vec3.divide(vec3.create(), vec3.fromValues(1, 1, 1), pixelSize)
        );

        const patientSpaceCenter = this.boundingBox.center;
        const imageSpaceCenter =
            this.image.toNormalizedImageSpace(patientSpaceCenter);

        imageSpaceCenter[0] = (imageSpaceCenter[0] + 1.0) * 0.5;
        imageSpaceCenter[1] = (imageSpaceCenter[1] + 1.0) * 0.5;
        const imageSliceIndex =
            (imageSpaceCenter[2] + 1.0) * 0.5 * (this.image.sizeZ - 1);
        imageSpaceCenter[2] = (imageSliceIndex + 0.5) / (this.image.sizeZ - 1);

        this.shader.mouseImageCoords.loadVec3(imageSpaceCenter);
        this.shader.similarityThreshold.loadFloat(this._similarityThreshold);
        if (this.image.windowLevel) {
            this.shader.windowLevel.loadVec2(this.image.windowLevel.asVec2);
        }
        this.shader.radius.loadFloat(this.diameter * 0.5);
        this.shader.margin.loadFloat(this.margin);
        this.shader.brushSizeMm.loadVec3(pixelSize);

        const imageToPatient = mat4.fromRotationTranslationScale(
            mat4.create(),
            quat.fromMat3(quat.create(), this._boundingBox.rotation),
            vec3.create(),
            vec3.scale(vec3.create(), this._boundingBox.size, 0.5)
        );
        mat4.invert(this.patientToImage, imageToPatient);

        // flood fill iterations
        const iterations = Math.ceil(
            Math.max(pixelSize[0], pixelSize[1], pixelSize[2]) * 0.5
        );
        const bbBottom = Math.floor(
            this.image.parent.grid.toVoxelSpace(this._boundingBox.min)[2]
        );
        const centerIndex =
            Math.floor(
                this.image.parent.grid.toVoxelSpace(this._boundingBox.center)[2]
            ) - bbBottom;

        // TODO: this is not needed after floor()s above
        let bottomLayer: number;
        let topLayer: number;
        if (Number.isInteger(centerIndex)) {
            bottomLayer = centerIndex - 1;
            topLayer = centerIndex + 1;
        } else {
            bottomLayer = Math.floor(centerIndex);
            topLayer = Math.ceil(centerIndex);
        }
        gl.clearColor(0, 0, 0, 1);

        for (let i = 0; i < iterations; ++i) {
            const read = this.buffers[~i & 1];
            read.bind();
            const write = this.buffers[i & 1];
            //TODO: Could clear slices once and loop through bottom to top
            for (let layer = 0; layer < pixelSize[2]; ++layer) {
                this.fbo.createTextureLayer(write.texture, layer);
                gl.clear(gl.COLOR_BUFFER_BIT);
                if (layer < bottomLayer || layer > topLayer) {
                    continue;
                }
                // TODO: suspect maths
                const normalPosition =
                    Math.floor(this._boundingBox.min[2]) +
                    layer * this.image.parent.grid.resZ;

                const patientSpaceCenterLayer = vec3.fromValues(
                    patientSpaceCenter[0],
                    patientSpaceCenter[1],
                    normalPosition
                );

                const imageSpaceCenter = this.image.toNormalizedImageSpace(
                    patientSpaceCenterLayer
                );
                imageSpaceCenter[0] = (imageSpaceCenter[0] + 1.0) * 0.5;
                imageSpaceCenter[1] = (imageSpaceCenter[1] + 1.0) * 0.5;
                const imageSliceIndex =
                    (imageSpaceCenter[2] + 1.0) * 0.5 * (this.image.sizeZ - 1);
                imageSpaceCenter[2] =
                    (imageSliceIndex + 0.5) / (this.image.sizeZ - 1);

                const scale = vec3.clone(this.boundingBox.size);
                scale[0] /= this.image.parent.grid.sizeXMM * 2.0;
                scale[1] /= this.image.parent.grid.sizeYMM * 2.0;
                scale[2] = 1;

                const toImageSpace = mat4.fromRotationTranslationScale(
                    mat4.create(),
                    quat.fromMat3(
                        quat.create(),
                        this.image.parent.grid.getOrientation()
                    ),
                    imageSpaceCenter,
                    scale
                );

                this.shader.toImageTextureCoords.loadMatrix(toImageSpace);

                this.shader.k.loadFloat((2 * (layer + 0.5)) / pixelSize[2] - 1);
                gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            }
            bottomLayer = Math.max(bottomLayer - 1, 0);
            topLayer = Math.min(topLayer + 1, pixelSize[2] - 1);
        }
        this.readIndex = ~iterations & 1;
        this.shader.stop();
        vao.unbind(0);
        this.fbo.unbind();
    }

    /**
     * @Override
     * Method to delete this {@link CustomTargetObject}.\
     * This calls the {@link CustomTargetObject.delete delete} method of the {@link CustomTargetObject} superclass, which deletes the {@link sdf}.\
     * But it also {@link FrameBufferObject.delete deletes} the {@link fbo} and shaders.\
     * It is important to call this method when the {@link TargetSphere} is no longer needed.
     * @returns True to indicate that the {@link CustomTargetObject} was successfully deleted.
     */
    public delete(): boolean {
        super.delete();
        for (const buffer of this.buffers) {
            buffer.delete();
        }
        this.shader.delete();
        this.outlineShader.delete();
        if (this.fbo) {
            this.fbo.delete();
        }
        return true;
    }
}

class FloodFillShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
    precision highp float;
    precision highp sampler3D;
    
    in vec2 position;

    out vec3 brushTextureCoords;
    out vec3 imageCoords;

    out float imageValue;
    
    //uniform vec2 scale;

    uniform sampler3D imageTexture;

    uniform float k; // value in between -1 and 1
    uniform mat4 toImageTextureCoords;

    uniform vec2 windowLevel;

    uniform vec3 mouseImageCoords;
    
    float valueModification(float value){
        value = (value - windowLevel.y + (windowLevel.x * 0.5)) / windowLevel.x;
        return clamp(value, 0.0, 1.0);
    }

    void main() {
        gl_Position = vec4(position, 0.0, 1.0);
        brushTextureCoords = vec3(position, k);
        imageCoords = (toImageTextureCoords * vec4(position.x, position.y, 0.0, 1.0)).xyz;
        imageValue = valueModification(texture(imageTexture, mouseImageCoords).r);
    }`;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
    precision highp float;
    precision highp sampler3D;

    in vec3 brushTextureCoords;
    in vec3 imageCoords;

    in float imageValue;

    out float value;
    
    uniform sampler3D brushTexture;
    uniform sampler3D imageTexture;
    //1 / pixelSize
    uniform vec3 brushResolution;
    //1 / pixelSize
    //uniform vec3 imageResolution;
    uniform float similarityThreshold;
    
    uniform float radius;
    uniform float margin;
    uniform vec3 brushSizeMm;

    uniform vec2 windowLevel;
    
    float valueModification(float value){
        value = (value - windowLevel.y + (windowLevel.x * 0.5)) / windowLevel.x;
        return clamp(value, 0.0, 1.0);
    }

    void main() {
        if(imageCoords.x < 0.0 || imageCoords.x > 1.0 || imageCoords.y < 0.0 || imageCoords.y > 1.0 || imageCoords.z < 0.0 || imageCoords.z > 1.0) {
            discard;
        } else if(abs(brushTextureCoords.x) < brushResolution.x && abs(brushTextureCoords.y) < brushResolution.y && abs(brushTextureCoords.z) < 2.0 * brushResolution.z){
            //the center has to be rendered 
            value = 1.0;
            return;
        } else {
            float distance = length(brushTextureCoords * 0.5 * brushSizeMm);
            float cap = 0.5 - 0.5 * (distance - radius) / margin;
            vec3 textureCoords = (brushTextureCoords + vec3(1.0)) * 0.5;
            value = texture(brushTexture, textureCoords).r;
            if(value < 0.5){
                for(float x = -1.0; x < 2.0; ++x){
                    for(float y = -1.0; y < 2.0; ++y){
                        for(float z = -1.0; z < 2.0; ++z){
                            if(z == 0.0 && y == 0.0 && x == 0.0) continue;
                            vec3 delta = vec3(x, y, z) * brushResolution;
                            float distance = 0.5 * length(delta * brushSizeMm) / margin;
                            vec3 neighborBrushCoords = textureCoords + delta;
                            float ref = texture(brushTexture, neighborBrushCoords).r;
                            if(ref > 0.5){
                                float refImageValue = valueModification(texture(imageTexture, imageCoords).r);
                                float deltaImage = abs(imageValue - refImageValue) / similarityThreshold;
                                value = max(1.0 - deltaImage, value);
                            } else {
                                value = max(ref - distance, value);
                            }
                        }
                    }
                }
            } else {
                value = 1.0;
                for(float x = -1.0; x < 2.0; ++x){
                    for(float y = -1.0; y < 2.0; ++y){
                        for(float z = -1.0; z < 2.0; ++z){
                            if(x == 0.0 && y == 0.0 && z == 0.0) {
                                continue;
                            }
                            vec3 delta = vec3(x, y, z) * brushResolution;
                            vec3 neighborBrushCoords = textureCoords + delta;
                            if(neighborBrushCoords.x < 0.0 || neighborBrushCoords.x > 1.0 || neighborBrushCoords.y < 0.0 || neighborBrushCoords.y > 1.0 || neighborBrushCoords.z < 0.0 || neighborBrushCoords.z > 1.0) {
                                continue;
                            }
                            float ref = texture(brushTexture, neighborBrushCoords).r;
                            float distance = 0.5 * length(delta * brushSizeMm) / margin;
                            if(ref < 0.5){
                                value = min(0.5 + distance, value);
                            } else {
                                value = min(ref + distance, value);
                            }
                        }
                    }
                }
            }
            value = min(value, cap);
        }
    }
    `;

    /**
     * Value between 0 and 1.\
     * Vertex coordinates are multiplied by this to get the viewport coordinates.\
     * The scale should increase with each iteration.
     */
    //public readonly scale: UniformVec2;

    public readonly toImageTextureCoords: UniformMatrix;

    public readonly k: UniformFloat;

    private readonly brushTexture: UniformSampler;

    private readonly imageTexture: UniformSampler;

    /**
     * {@link UniformVec3} that provides the resolution of the brush.\
     * The unit is 1/pixelSize.
     */
    public readonly brushResolution: UniformVec3;

    /**
     * {@link UniformVec3} that provides the resolution of the image.\
     * The unit is 1/pixelSize.
     */
    //public readonly imageResolution: UniformVec3;

    public readonly mouseImageCoords: UniformVec3;

    public readonly radius: UniformFloat;
    public readonly margin: UniformFloat;
    public readonly brushSizeMm: UniformVec3;

    /**
     * Threshold value that determines if the neighboring pixels are similar enough to be considered part of the target volume.\
     * This should be a value in between 0 and 1.
     */
    public readonly similarityThreshold: UniformFloat;

    public readonly windowLevel: UniformVec2;

    public constructor() {
        super(
            FloodFillShader.VERTEX_CONTENT,
            FloodFillShader.FRAGMENT_CONTENT,
            'position'
        );
        //this.scale = new UniformVec2("scale");
        this.toImageTextureCoords = new UniformMatrix('toImageTextureCoords');
        this.k = new UniformFloat('k');
        this.brushTexture = new UniformSampler('brushTexture');
        this.imageTexture = new UniformSampler('imageTexture');
        this.radius = new UniformFloat('radius');
        this.margin = new UniformFloat('margin');
        this.brushSizeMm = new UniformVec3('brushSizeMm');
        this.brushResolution = new UniformVec3('brushResolution');
        this.mouseImageCoords = new UniformVec3('mouseImageCoords');
        //this.imageResolution = new UniformVec3("imageResolution");
        this.similarityThreshold = new UniformFloat('similarityThreshold');
        this.windowLevel = new UniformVec2('windowLevel');
        super.storeUniformLocations(
            this.toImageTextureCoords,
            this.k,
            this.brushTexture,
            this.imageTexture,
            this.brushResolution,
            this.mouseImageCoords,
            this.radius,
            this.margin,
            this.brushSizeMm,
            /*this.imageResolution, */ this.similarityThreshold,
            this.windowLevel
        );
        super.start();
        this.brushTexture.loadTextureUnit(0);
        this.imageTexture.loadTextureUnit(1);
        super.stop();
    }
}

/**
 *
 */
class OutlineShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es

    precision highp float;

    in vec2 position;
    in vec3 textureCoords;

    out vec3 pass_textureCoordinates;

    void main(){
        gl_Position = vec4(position, 0.0, 1.0);
        pass_textureCoordinates = textureCoords;
    }
    `;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
    
    precision highp float;
    precision highp sampler3D;

    in vec3 pass_textureCoordinates;

    out vec4 outColor;

    uniform vec4 color;
    uniform sampler3D sampler;

    const float diagonal = 0.70710678118;
    uniform mat4 viewToTexture;
    uniform vec2 relativeDist;

    bool isWithin(float x, float y) {
        vec4 rotated = viewToTexture * vec4(x * relativeDist.x, y * relativeDist.y, 0.0, 1.0);
        vec3 dest = pass_textureCoordinates + rotated.xyz;
        if(dest.x <= 0.0 || dest.x >= 1.0 || dest.y <= 0.0 || dest.y >= 1.0 || dest.z <= 0.0 || dest.z >= 1.0){
            return false;
        }
        return texture(sampler, dest).r >= 0.5;
    }

    void main(){
        bool within = texture(sampler, pass_textureCoordinates).r >= 0.5;

        if(
        isWithin(-diagonal, -diagonal) != within ||
        isWithin(0.0, -1.0) != within ||
        isWithin(diagonal, -diagonal) != within ||
        isWithin(-1.0, 0.0) != within ||
        isWithin(1.0, 0.0) != within ||
        isWithin(-diagonal, diagonal) != within ||
        isWithin(0.0, 1.0) != within ||
        isWithin(diagonal, diagonal) != within){
            outColor = color;
        } else {
            //outColor = texture(sampler, pass_textureCoordinates);
            discard;
        }
    }
    `;

    /**
     * {@link UniformVec4} that takes the line color as an input and provides it to {@link ContourTextureShader}.\
     * That color value is used as is to color the contour outlines.
     */
    public readonly color: UniformVec4;

    /**
     * {@link UniformMatrix} that provides a {@link ReadonlyMat4 Matrix} to convert distances from ViewSpace to TextureSpace.\
     * This is used to convert the {@link ContourRenderer.lineWidth lineWidth} into texture units.
     */
    public readonly viewToTexture: UniformMatrix;

    /**
     * {@link UniformVec2} that provides a {@link ReadonlyVec2 Vector} which represents the {@link ContourRenderer.lineWidth lineWidth} in ViewSpace.
     */
    public readonly relativeDist: UniformVec2;

    constructor() {
        super(
            OutlineShader.VERTEX_CONTENT,
            OutlineShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );
        this.color = new UniformVec4('color');
        this.viewToTexture = new UniformMatrix('viewToTexture');
        this.relativeDist = new UniformVec2('relativeDist');
        this.storeUniformLocations(
            this.color,
            this.viewToTexture,
            this.relativeDist
        );
    }
}
